import type { AdDetails } from '@amzn/safeframe-model';
import { isEmptyObject } from '../components/util.js';
import { getCreativeTemplateNameForTagging } from '../components/metrics/metric-tagger.js';
import { addJSONQueryParamToURL } from '../components/urlQueryParams/addJSONQueryParamToURL.js';
import { buildPixelUrl, firePixel } from '../components/pixel.js';
import { GLOBAL_IMPRESSION_FIRED_COUNTER } from '../components/counters/IMPRESSION_COUNTERS.js';
import { IMPRESSION as BTRIMPRESSION } from '../components/counters/BTR_COUNTERS.js';
import { IMPRESSION as CODIMPRESSION } from '../components/counters/COD_COUNTERS.js';
import { IMP, UNSERVED_VIEWABILITY, VIEWABILITY } from '../components/counters/AD_LOAD_CSM_COUNTERS.js';
import * as AD_LOAD_EVENTS from '../components/events/AD_LOAD_CSA_EVENTS.js';
import type { ViewabilityStandard, ViewableInfo } from '@amzn/safe-frame-client-amzn';
import { ViewableTimeCalculator } from './viewableTimeCalculator.js';
import { evaluateCreativeBeginToRender } from '@amzn/creative-rendering-monitor/src/creativeBTREvaluatorSync.js';
import { BEGIN_TO_RENDER_CLIENT, COUNT_ON_DOWNLOAD_CLIENT, MEASUREMENT_METHODS } from './MEASUREMENT_METHODS.js';
import { ClientApis } from './types/types.js';
import { PUBLISHER_JSON_KEY } from '../components/aax/aax.js';

type ViewedPixel = {
    viewed?: boolean;
    timeout?: number | null;
};
export type ViewableTimeCalculators = { [key: string]: ViewableTimeCalculator };

export class ClientReporter {
    constructor(
        readonly c: ClientApis,
        readonly o: AdDetails,
    ) {}

    // Viewability flags
    private readonly viewedPixels: { [key: string]: ViewedPixel } = {};
    /**
     * Tracks the amount of time a creative been considered viewable by viewability definitions.
     * Each key is a viewability definition - e.g. 'iab'
     */
    public readonly viewableTimeCalculators: ViewableTimeCalculators = {};

    fireCodImpressionPixel = async () => {
        let isPixelFired = false;
        isPixelFired = await firePixel(this.c, {
            id: 'ape_impression',
            pixelUrl: createPixelUrl(this.o.aaxImpPixelUrl, PUBLISHER_JSON_KEY, COUNT_ON_DOWNLOAD_CLIENT),
            baseCounterName: IMP,
            baseMetricName: AD_LOAD_EVENTS.IMP,
            logId: 'imp:cod',
            aaxProgramId: this.o.adCreativeMetaData.adProgramId,
        });

        if (isPixelFired) {
            // TODO: Migrate these metrics to the new pixel standard
            // https://code.amazon.com/packages/D16GSafeFrame/blobs/544913ede51c527b81aff887c5744915092be10e/--/packages/@amzn/d16gsafeframe/src/js/components/pixels/btrPixel.ts#L9
            this.emitImpressionPixelMetrics(false);
        }
    };

    /**
     * @desc [Internal] Checks if viewable info for a video creative meets viewability standards,
     *   and if so fires viewability pixels
     * @param cachedViewability - The percentage of a creative that is currently in the viewport
     * @param viewableInfo
     * @param viewabilityStandards - Each standard must be met before its
     *   corresponding viewability pixel can be fired
     * @param isNoInventory - Indicates whether any ad was returned or not
     * @param playingTimeInSeconds - video playing time without user-initiated interruptions
     */
    fireViewablePixelsForVideo = (
        cachedViewability: number | null,
        viewableInfo: ViewableInfo,
        viewabilityStandards: ViewabilityStandard[],
        isNoInventory: boolean,
        playingTimeInSeconds: number | null,
    ) => {
        if (isEmptyObject(this.o) || isEmptyObject(this.c) || !this.o.aaxInstrPixelUrl || !viewabilityStandards) {
            return;
        }
        const meetsViewabilityStandard = (
            viewableTime: number,
            playingTime: number | null,
            viewabilityStandard: ViewabilityStandard,
        ) => {
            const meetsViewableTimeRequirement = viewableTime >= viewabilityStandard.t;
            const meetsPlayingTimeRequirement = playingTime && playingTime >= viewabilityStandard.t;
            return meetsViewableTimeRequirement && meetsPlayingTimeRequirement;
        };
        const fireViewablePixel = (v: ViewabilityStandard) => {
            viewableInfo.v = v;
            const ps = window.aPageStart;
            const as = window.adStartTime;
            viewableInfo.ptv = ps ? (Date.now() - ps) / 1000 : 0;
            viewableInfo.ttv = as ? (Date.now() - as) / 1000 : 0;
            if (isNoInventory) {
                viewableInfo.niv = true;
            }
            let pixelUrl;
            if (!isNoInventory) {
                pixelUrl = buildPixelUrl(this.o.aaxInstrPixelUrl, 'v/', viewableInfo);
                firePixel(this.c, {
                    id: 'ape_viewability' + '_' + v.def,
                    pixelUrl,
                    baseCounterName: VIEWABILITY,
                    baseMetricName: AD_LOAD_EVENTS.VIEWABILITY,
                    counterExtension: v.def,
                    logId: `view:${v.def}`,
                    aaxProgramId: this.o.adCreativeMetaData.adProgramId,
                });
            } else {
                pixelUrl = buildPixelUrl(this.o.aaxInstrPixelUrl, 'niv/', viewableInfo);
                firePixel(this.c, {
                    id: 'ape_ni_viewability' + '_' + v.def,
                    pixelUrl,
                    baseCounterName: UNSERVED_VIEWABILITY,
                    baseMetricName: AD_LOAD_EVENTS.UNSERVED_VIEWABILITY,
                    counterExtension: v.def,
                    logId: 'view:niv',
                    aaxProgramId: this.o.adCreativeMetaData.adProgramId,
                });
            }
        };
        const measureViewability = (viewabilityStandard: ViewabilityStandard, viewablePercentage: number | null) => {
            const def = viewabilityStandard.def;
            if (viewablePercentage && isViewable(viewablePercentage, viewabilityStandard.p)) {
                this.viewableTimeCalculators[def].transitionToViewableState(Date.now());
            } else {
                this.viewableTimeCalculators[def].transitionToNotViewableState();
            }
            const viewableTimeInSeconds = this.viewableTimeCalculators[def].getViewableTimeInSeconds(Date.now());
            const shouldFire =
                !this.viewedPixels[def].viewed &&
                meetsViewabilityStandard(viewableTimeInSeconds, playingTimeInSeconds, viewabilityStandard);
            if (shouldFire) {
                this.viewedPixels[def].viewed = true;
                fireViewablePixel(viewabilityStandard);
            }
        };
        for (let i = 0; i < viewabilityStandards.length; i++) {
            const def = viewabilityStandards[i].def;
            if (def) {
                this.viewedPixels[def] = this.viewedPixels[def] || {};
                this.viewedPixels[def].viewed =
                    typeof this.viewedPixels[def].viewed === 'undefined' ? false : this.viewedPixels[def].viewed;
                if (typeof this.viewableTimeCalculators[def] === 'undefined') {
                    this.viewableTimeCalculators[def] = new ViewableTimeCalculator();
                }
            }
        }
        // Fire pixels following this standard: https://w.amazon.com/index.php/A9/AAX/Specifications/AAXLogFormatV2_0#Pixel_View_Payload_Log
        for (let i = 0; i < viewabilityStandards.length; i++) {
            measureViewability(viewabilityStandards[i], cachedViewability);
        }
    };

    /**
     * @method fireViewablePixels
     * @desc [Internal] Checks if viewable info for a non-video creative meets viewability standards,
     *   and if so fires viewability pixels
     * @param {Number} cachedViewability - The percentage of a creative that is currently in the viewport
     * @param {Object} viewableInfo
     * @param {Array.<ViewabilityStandard>} viewabilityStandards - Each standard must be met before its
     *   corresponding viewability pixel can be fired
     * @param {Boolean} isNoInventory - Indicates whether any ad was returned or not
     * @param {Date} renderCompleteTime - The date to use for render time completion. Use this to calculate
     */
    fireViewablePixels = (
        cachedViewability: number | null,
        viewableInfo: ViewableInfo,
        viewabilityStandards: ViewabilityStandard[],
        isNoInventory: boolean,
    ) => {
        const fireViewablePixel = (v: ViewabilityStandard) => {
            viewableInfo.v = v;
            const ps = window.aPageStart;
            const as = window.adStartTime;
            viewableInfo.ptv = ps ? (Date.now() - ps) / 1000 : 0;
            viewableInfo.ttv = as ? (Date.now() - as) / 1000 : 0;
            if (isNoInventory) {
                viewableInfo.niv = true;
            }
            let pixelUrl;
            if (!isNoInventory) {
                pixelUrl = buildPixelUrl(this.o.aaxInstrPixelUrl, 'v/', viewableInfo);
                firePixel(this.c, {
                    id: 'ape_viewability' + '_' + v.def,
                    pixelUrl,
                    baseCounterName: VIEWABILITY,
                    baseMetricName: AD_LOAD_EVENTS.VIEWABILITY,
                    counterExtension: v.def,
                    logId: `view:${v.def}`,
                    aaxProgramId: this.o.adCreativeMetaData.adProgramId,
                });
            } else {
                pixelUrl = buildPixelUrl(this.o.aaxInstrPixelUrl, 'niv/', viewableInfo);
                firePixel(this.c, {
                    id: 'ape_ni_viewability' + '_' + v.def,
                    pixelUrl,
                    baseCounterName: UNSERVED_VIEWABILITY,
                    baseMetricName: AD_LOAD_EVENTS.UNSERVED_VIEWABILITY,
                    counterExtension: v.def,
                    logId: 'view:niv',
                    aaxProgramId: this.o.adCreativeMetaData.adProgramId,
                });
            }
        };
        const measureViewability = (viewabilityStandard: ViewabilityStandard) => {
            const p = viewabilityStandard.p;
            const t = viewabilityStandard.t;
            const def = viewabilityStandard.def;
            if (!this.viewedPixels[def].viewed && cachedViewability && isViewable(cachedViewability, p)) {
                if (!this.viewedPixels[def].timeout) {
                    this.viewedPixels[def].timeout = (setTimeout as typeof window.setTimeout)(() => {
                        this.viewedPixels[def].viewed = true;
                        fireViewablePixel(viewabilityStandard);
                    }, 1000 * t);
                }
            } else if (this.viewedPixels[def].timeout) {
                (clearTimeout as typeof window.clearTimeout)(this.viewedPixels[def].timeout as number);
                this.viewedPixels[def].timeout = null;
            }
        };
        // Fire pixels following this standard: https://w.amazon.com/index.php/A9/AAX/Specifications/AAXLogFormatV2_0#Pixel_View_Payload_Log
        for (const vs of viewabilityStandards) {
            const def = vs.def;
            if (!this.viewedPixels?.[def]) {
                this.viewedPixels[def] = { viewed: false, timeout: null };
            }
            measureViewability(vs);
        }
    };

    private emitImpressionPixelMetrics(isBtrPixel: boolean) {
        const adProgramId = this.o.adCreativeMetaData.adProgramId ? this.o.adCreativeMetaData.adProgramId : 'unknown';
        const creativeTemplateNameForMetrics = getCreativeTemplateNameForTagging(this.o);
        this.c.countMetric(GLOBAL_IMPRESSION_FIRED_COUNTER, 1, true);
        this.c.countMetric(GLOBAL_IMPRESSION_FIRED_COUNTER + ':program:' + adProgramId, 1, true);
        this.c.countMetric(GLOBAL_IMPRESSION_FIRED_COUNTER + ':template:' + creativeTemplateNameForMetrics, 1, true);
        const impressionMetric = isBtrPixel ? BTRIMPRESSION : CODIMPRESSION;
        this.c.countMetric(impressionMetric, 1, false);
        this.c.countMetric(impressionMetric, 1, true);
        this.c.countMetric(impressionMetric + ':program:' + adProgramId, 1, true);
        this.c.countMetric(impressionMetric + ':template:' + creativeTemplateNameForMetrics, 1, true);
    }
}

// Helper method to check if ad is viewable or not and handle the edge case when p equals 0
const isViewable = (viewablePercentage: number, p: number) => {
    return (100 * viewablePercentage >= p && p !== 0) || (100 * viewablePercentage > p && p === 0);
};

// Creates the filled out impression tracking URL
export const createPixelUrl = (
    aaxImpPixelUrl: string,
    queryParamKey: string,
    measurementMethod: MEASUREMENT_METHODS,
) => {
    const paets = aaxImpPixelUrl.includes('.paets.');
    const legacyAnswer = addJSONQueryParamToURL(aaxImpPixelUrl, queryParamKey, {
        measurementMethod: measurementMethod,
    });
    return paets ? aaxImpPixelUrl : legacyAnswer;
};

export const tryWaitForBTR = async (element: HTMLElement = document.body): Promise<MEASUREMENT_METHODS> => {
    const btrEvaluationWaitTime = 75;
    let elapsedTime = 0;
    const intervalMs = 5;

    return new Promise<MEASUREMENT_METHODS>((resolve) => {
        const intervalId = setInterval(() => {
            elapsedTime += intervalMs;

            const response = evaluateCreativeBeginToRender(element);
            if (response.success) {
                clearInterval(intervalId);
                resolve(BEGIN_TO_RENDER_CLIENT);
            } else if (elapsedTime >= btrEvaluationWaitTime) {
                clearInterval(intervalId);
                resolve(COUNT_ON_DOWNLOAD_CLIENT);
            }
        }, intervalMs);
    });
};
